import { useMemo } from "react";
import {
  ORDINAL_CHOICES,
  ordinalLabel,
  ordinalListLabel,
  deriveFromDate,
  defaultRecurrenceConfig,
  type RecurrenceConfig,
} from "@/lib/recurrenceConfig";
import { Repeat } from "lucide-react";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { cn } from "../../lib/utils";
import { DAY_NAMES } from "convex/recurrenceLabel";

// Compute the first rule-matching date on or after `afterDate` given the config and startDate rule
function getFirstRuleDate(config: RecurrenceConfig, startDate: string): string {
  const { dayOfWeek, dayOfMonth, ordinal } = deriveFromDate(startDate);
  const afterDate = config.firstEventDiffers && config.firstEventDate ? config.firstEventDate : startDate;
  const after = new Date(afterDate + "T00:00:00");

  if (config.period === "weekly") {
    const startDay = after.getUTCDay();
    const daysUntil = (dayOfWeek - startDay + 7) % 7;
    const result = new Date(after);
    result.setUTCDate(result.getUTCDate() + daysUntil);
    return result.toISOString().split("T")[0]!;
  }

  // Monthly
  const ordinals = config.ordinals.length > 0 ? config.ordinals : [ordinal];
  let year = after.getUTCFullYear();
  let month = after.getUTCMonth();
  for (let i = 0; i < 24; i++) {
    // A month can contribute several candidates ("2nd and 4th Sunday"), so
    // take the soonest one that has not already passed.
    let candidates: Date[];
    if (config.monthlyType === "day_of_month") {
      const daysInMonth = new Date(year, month + 1, 0).getDate();
      const day = Math.min(dayOfMonth, daysInMonth);
      candidates = [new Date(Date.UTC(year, month, day))];
    } else {
      candidates = ordinals.map((nth) => {
        const firstDayOfWeek = new Date(Date.UTC(year, month, 1)).getUTCDay();
        const daysUntilTarget = (dayOfWeek - firstDayOfWeek + 7) % 7;
        return new Date(Date.UTC(year, month, 1 + daysUntilTarget + (nth - 1) * 7));
      });
    }
    const match = candidates
      .filter((c) => c >= after)
      .sort((a, b) => a.getTime() - b.getTime())[0];
    if (match) return match.toISOString().split("T")[0]!;
    month++;
    if (month > 11) { month = 0; year++; }
  }
  return afterDate;
}

function buildPreview(config: RecurrenceConfig, startDate: string): string {
  const { dayOfWeek, dayOfMonth, ordinal } = deriveFromDate(startDate);
  const dayName = DAY_NAMES[dayOfWeek]!;
  const n = config.interval;
  const every = n === 1 ? "Every" : `Every ${n}`;
  const unit = config.period === "weekly"
    ? (n === 1 ? "week" : "weeks")
    : (n === 1 ? "month" : "months");

  let ruleText: string;
  if (config.period === "weekly") {
    ruleText = `${every} ${unit} on ${dayName}`;
  } else if (config.monthlyType === "day_of_month") {
    const suffix = ["st", "nd", "rd"][(((dayOfMonth % 100) - 20) % 10) - 1] ?? "th";
    ruleText = `${every} ${unit} on the ${dayOfMonth}${suffix}`;
  } else {
    const ordinals = config.ordinals.length > 0 ? config.ordinals : [ordinal];
    ruleText = `${every} ${unit} on the ${ordinalListLabel(ordinals)} ${dayName}`;
  }

  if (config.firstEventDiffers && config.firstEventDate) {
    const firstRuleDate = getFirstRuleDate(config, startDate);
    return `${config.firstEventDate}, then ${ruleText.toLowerCase()} starting ${firstRuleDate}`;
  }
  return ruleText;
}

interface RecurrenceSelectorProps {
  value: RecurrenceConfig | null;
  onChange: (config: RecurrenceConfig | null) => void;
  startDate: string;
  /**
   * Editing the schedule of a series that already recurs. There is nothing to
   * switch on or off — a series stops by being archived, not by having its
   * recurrence toggled away — so the switch is replaced by a plain heading.
   */
  locked?: boolean;
  /** Heading text; defaults to the create-time wording. */
  title?: string;
}

export {
  defaultRecurrenceConfig,
  toRecurrenceArgs,
  fromRecurrenceArgs,
  type RecurrenceConfig,
} from "@/lib/recurrenceConfig";

export function RecurrenceSelector({
  value,
  onChange,
  startDate,
  locked = false,
  title,
}: RecurrenceSelectorProps) {
  const enabled = value !== null;
  const { dayOfWeek, dayOfMonth, ordinal } = useMemo(() => deriveFromDate(startDate), [startDate]);
  const dayName = DAY_NAMES[dayOfWeek]!;
  const dayOrdLabel = ordinalLabel(ordinal);

  const handleToggle = () => {
    onChange(enabled ? null : defaultRecurrenceConfig(startDate));
  };

  const update = (partial: Partial<RecurrenceConfig>) => {
    if (!value) return;
    onChange({ ...value, ...partial });
  };

  const previewText = useMemo(
    () => (value ? buildPreview(value, startDate) : ""),
    [value, startDate]
  );

  return (
    <div className="rounded-xl border border-border bg-card p-4 space-y-4">
      {locked ? (
        <div className="flex items-center gap-2.5">
          <Repeat className="w-4 h-4 text-muted-foreground" />
          <span className="text-sm font-medium text-foreground">
            {title ?? "Schedule"}
          </span>
        </div>
      ) : (
        <button
          type="button"
          onClick={handleToggle}
          className="w-full flex items-center justify-between"
        >
          <div className="flex items-center gap-2.5">
            <Repeat className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm font-medium text-foreground">
              {title ?? "Recurring event"}
            </span>
          </div>
          <div className={cn(
            "relative w-10 h-6 rounded-full transition-colors duration-200",
            enabled ? "bg-primary" : "bg-secondary border border-border"
          )}>
            <div className={cn(
              "absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-transform duration-200",
              enabled ? "translate-x-5" : "translate-x-1"
            )} />
          </div>
        </button>
      )}

      {enabled && value && (
        <div className="space-y-4 pt-1">
          {/* Repeat every N period */}
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Repeats</Label>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground shrink-0">Every</span>
              <Input
                type="number"
                min={1}
                max={12}
                value={value.interval}
                onChange={(e) => update({ interval: Math.max(1, parseInt(e.target.value) || 1) })}
                className="w-16 text-center"
              />
              <select
                value={value.period}
                onChange={(e) => update({ period: e.target.value as "weekly" | "monthly" })}
                className="border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
              >
                <option value="weekly">{value.interval === 1 ? "week" : "weeks"}</option>
                <option value="monthly">{value.interval === 1 ? "month" : "months"}</option>
              </select>
            </div>
          </div>

          {/* Monthly sub-options */}
          {value.period === "monthly" && (
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">On</Label>
              <label className="flex items-center gap-2.5 cursor-pointer">
                <input
                  type="radio"
                  name="monthlyType"
                  checked={value.monthlyType === "day_of_month"}
                  onChange={() => update({ monthlyType: "day_of_month" })}
                  className="accent-primary"
                />
                <span className="text-sm text-foreground">
                  Day {dayOfMonth} of the month
                </span>
              </label>
              <label className="flex items-center gap-2.5 cursor-pointer">
                <input
                  type="radio"
                  name="monthlyType"
                  checked={value.monthlyType === "nth_weekday"}
                  onChange={() => update({ monthlyType: "nth_weekday" })}
                  className="accent-primary"
                />
                <span className="text-sm text-foreground">
                  A {dayName} of the month
                </span>
              </label>

              {/* Which weeks — a dance can run on more than one, e.g. 2nd and 4th */}
              {value.monthlyType === "nth_weekday" && (
                <div className="pl-6 space-y-1.5">
                  <div className="flex flex-wrap gap-1.5">
                    {ORDINAL_CHOICES.map((nth) => {
                      const selected = value.ordinals.includes(nth);
                      return (
                        <button
                          key={nth}
                          type="button"
                          aria-pressed={selected}
                          onClick={() =>
                            update({
                              ordinals: selected
                                ? value.ordinals.filter((o) => o !== nth)
                                : [...value.ordinals, nth].sort((a, b) => a - b),
                            })
                          }
                          className={cn(
                            "px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors",
                            selected
                              ? "bg-primary text-primary-foreground border-primary"
                              : "bg-background text-foreground border-border hover:bg-secondary"
                          )}
                        >
                          {ordinalLabel(nth)}
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {value.ordinals.length === 0
                      ? `Pick which ${dayName}s — defaults to the ${dayOrdLabel}.`
                      : `${ordinalListLabel(value.ordinals)} ${dayName} of each month.`}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* First event exception */}
          <label className="flex items-start gap-2.5 cursor-pointer">
            <input
              type="checkbox"
              checked={value.firstEventDiffers}
              onChange={(e) => update({
                firstEventDiffers: e.target.checked,
                firstEventDate: e.target.checked ? (value.firstEventDate || startDate) : startDate,
              })}
              className="mt-0.5 accent-primary"
            />
            <span className="text-sm text-foreground">First event is on a different date</span>
          </label>

          {value.firstEventDiffers && (
            <div className="space-y-1.5 pl-6">
              <Label className="text-xs text-muted-foreground">First event date</Label>
              <Input
                type="date"
                value={value.firstEventDate}
                onChange={(e) => update({ firstEventDate: e.target.value })}
                className="w-44"
              />
            </div>
          )}

          {/* End date */}
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Ends on (optional)</Label>
            <Input
              type="date"
              value={value.endDate}
              onChange={(e) => update({ endDate: e.target.value })}
              min={startDate}
              className="w-44"
            />
          </div>

          {/* Preview */}
          {previewText && (
            <p className="text-xs text-primary font-medium">{previewText}</p>
          )}
        </div>
      )}
    </div>
  );
}
